import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Calendar, 
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  Database,
  Filter,
  Download,
  Upload,
  Settings,
  Eye
} from "lucide-react";
import { 
  PriceUpdateSchedule, 
  PriceUpdateLog
} from "@/types";

const UpdateSchedule = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  const [priceSchedules] = useState<PriceUpdateSchedule[]>([
    {
      id: "1",
      materialCategory: "Cement",
      updateFrequency: "quarterly",
      nextUpdateDate: "2024-04-01",
      lastUpdateDate: "2024-01-01",
      isActive: true,
      autoUpdate: true,
      updateSource: "calculation",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "2",
      materialCategory: "Steel",
      updateFrequency: "quarterly",
      nextUpdateDate: "2024-04-01",
      lastUpdateDate: "2024-01-01",
      isActive: true,
      autoUpdate: true,
      updateSource: "calculation",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "3",
      materialCategory: "Aggregates",
      updateFrequency: "monthly",
      nextUpdateDate: "2024-02-01",
      lastUpdateDate: "2024-01-01",
      isActive: true,
      autoUpdate: false,
      updateSource: "manual",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    }
  ]);

  const [updateLogs] = useState<PriceUpdateLog[]>([
    {
      id: "1",
      updateType: "quarterly",
      materialCategory: "Cement",
      geoZoneId: "LAG",
      quarter: "2024-Q1",
      recordsUpdated: 15,
      recordsCreated: 5,
      updateStatus: "completed",
      startedAt: "2024-01-01T09:00:00Z",
      completedAt: "2024-01-01T09:15:00Z",
      createdAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "2",
      updateType: "quarterly",
      materialCategory: "Steel",
      geoZoneId: "ABJ",
      quarter: "2024-Q1",
      recordsUpdated: 12,
      recordsCreated: 3,
      updateStatus: "completed",
      startedAt: "2024-01-01T09:30:00Z",
      completedAt: "2024-01-01T09:45:00Z",
      createdAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "3",
      updateType: "manual",
      materialCategory: "Aggregates",
      geoZoneId: "KAN",
      quarter: "2024-Q1",
      recordsUpdated: 8,
      recordsCreated: 2,
      updateStatus: "in_progress",
      startedAt: "2024-01-01T10:00:00Z",
      completedAt: null,
      createdAt: "2024-01-01T00:00:00Z"
    }
  ]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'in_progress':
        return <Badge variant="default" className="bg-blue-100 text-blue-800"><Clock className="h-3 w-3 mr-1" />In Progress</Badge>;
      case 'failed':
        return <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  const filteredSchedules = priceSchedules.filter(schedule => {
    const matchesCategory = selectedCategory === "all" || schedule.materialCategory === selectedCategory;
    const matchesStatus = selectedStatus === "all" || 
      (selectedStatus === "active" && schedule.isActive) ||
      (selectedStatus === "inactive" && !schedule.isActive);
    return matchesCategory && matchesStatus;
  });

  const filteredLogs = updateLogs.filter(log => {
    const matchesCategory = selectedCategory === "all" || log.materialCategory === selectedCategory;
    const matchesStatus = selectedStatus === "all" || log.updateStatus === selectedStatus;
    return matchesCategory && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Price Update</h2>
          <p className="text-gray-600">Automated and manual price update scheduling</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Data
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filters & Controls
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="category">Material Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Cement">Cement</SelectItem>
                  <SelectItem value="Steel">Steel</SelectItem>
                  <SelectItem value="Aggregates">Aggregates</SelectItem>
                  <SelectItem value="Blocks">Blocks</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="action">Action</Label>
              <Button className="w-full" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Schedule Update
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Schedules</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{priceSchedules.length}</div>
            <p className="text-xs text-muted-foreground">Active schedules</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Auto Updates</CardTitle>
            <RefreshCw className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{priceSchedules.filter(s => s.autoUpdate).length}</div>
            <p className="text-xs text-muted-foreground">Automated</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Next Update</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Feb 1</div>
            <p className="text-xs text-muted-foreground">Aggregates</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed Today</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{updateLogs.filter(l => l.updateStatus === 'completed').length}</div>
            <p className="text-xs text-muted-foreground">Updates</p>
          </CardContent>
        </Card>
      </div>

      {/* Price Update Schedules */}
      <Card>
        <CardHeader>
          <CardTitle>Price Update Schedules</CardTitle>
          <CardDescription>Automated and manual price update scheduling</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Material Category</TableHead>
                <TableHead>Frequency</TableHead>
                <TableHead>Next Update</TableHead>
                <TableHead>Last Update</TableHead>
                <TableHead>Auto Update</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSchedules.map((schedule) => (
                <TableRow key={schedule.id}>
                  <TableCell className="font-medium">{schedule.materialCategory}</TableCell>
                  <TableCell className="capitalize">{schedule.updateFrequency}</TableCell>
                  <TableCell>{new Date(schedule.nextUpdateDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {schedule.lastUpdateDate && new Date(schedule.lastUpdateDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Badge variant={schedule.autoUpdate ? 'default' : 'secondary'}>
                      {schedule.autoUpdate ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={schedule.isActive ? 'default' : 'secondary'}>
                      {schedule.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm">
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Update Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Update Logs</CardTitle>
          <CardDescription>Recent price update activities and status</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Update Type</TableHead>
                <TableHead>Material Category</TableHead>
                <TableHead>Geo Zone</TableHead>
                <TableHead>Quarter</TableHead>
                <TableHead>Records Updated</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Duration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="capitalize">{log.updateType}</TableCell>
                  <TableCell>{log.materialCategory}</TableCell>
                  <TableCell>{log.geoZoneId}</TableCell>
                  <TableCell>{log.quarter}</TableCell>
                  <TableCell>{log.recordsUpdated}</TableCell>
                  <TableCell>{getStatusBadge(log.updateStatus)}</TableCell>
                  <TableCell>
                    {log.completedAt && log.startedAt && 
                      `${Math.round((new Date(log.completedAt).getTime() - new Date(log.startedAt).getTime()) / 1000 / 60)}m`
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default UpdateSchedule;
